/**
 * QUIL — Local-first storage API
 * All writes: 1) Dexie immediately, 2) enqueue OneDrive upload.
 * The app always reads from Dexie; sync.ts drains the queue in the background.
 */

import type { Table } from 'dexie'
import { uuid } from './uuid'
import { db } from './db'
import type {
  Work, Chapter, Scene, Character, Pairing, PlotPoint, CanonNote, SyncQueueEntry,
  AggregateEntityType, Snapshot, SnapshotReason,
} from './db'
import { QUIL_ONEDRIVE_ROOT } from './auth'
import { countProseWords } from './text'
import { logSync } from './syncMonitor'
// Synced entity stamps (updatedAt / tombstone deletedAt) use the server-anchored
// clock so cross-device LWW is immune to wall-clock skew (lib/clock.ts).
import { serverNow } from './clock'
import { deviceNameRef } from './deviceName'

// ─── Sync queue helpers ───────────────────────────────────────────────────────

type QueuePayload = Omit<SyncQueueEntry, 'id' | 'createdAt' | 'retryCount' | 'nextRetryAt' | 'errorMessage'>

/**
 * Window event fired whenever the sync queue gains work, so the sync engine can
 * drain it promptly instead of waiting for the next visibility/online tick.
 * Decouples storage from the auth-held getToken (sync.ts listens for this).
 */
export const QUEUE_UPDATED_EVENT = 'quil:queue-updated'

function notifyQueueUpdated(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(QUEUE_UPDATED_EVENT))
  }
}

/**
 * Add or replace a queue entry for the given (entityId, remotePath) pair.
 * Replacing means rapid saves don't pile up redundant uploads.
 */
async function enqueueUpload(entry: QueuePayload): Promise<void> {
  // Remove any existing pending entry for the same file
  const existing = await db.syncQueue.where('entityId').equals(entry.entityId).toArray()
  const dup = existing.find(e => e.remotePath === entry.remotePath)
  if (dup?.id != null) await db.syncQueue.delete(dup.id)

  await db.syncQueue.add({
    ...entry,
    retryCount: 0,
    nextRetryAt: 0,
    createdAt: Date.now(),   // queue-local bookkeeping — device time, never synced
  })

  notifyQueueUpdated()
}

// ─── Path helpers ─────────────────────────────────────────────────────────────

const workMetaPath = (workId: string) =>
  `${QUIL_ONEDRIVE_ROOT}/works/${workId}/meta.json`

// Prose is TipTap JSON, so the file says so: {id}.prose.json beside {id}.json
// (meta). Renamed from the misleading {id}.md pre-launch (Kamrin 2026-07-13) —
// no migration: OneDrive is being wiped for a fresh start before first real use.
const chapterProsePath = (workId: string, chapterId: string) =>
  `${QUIL_ONEDRIVE_ROOT}/works/${workId}/chapters/${chapterId}.prose.json`

const chapterMetaPath = (workId: string, chapterId: string) =>
  `${QUIL_ONEDRIVE_ROOT}/works/${workId}/chapters/${chapterId}.json`

const aggregatePath = (workId: string, filename: string) =>
  `${QUIL_ONEDRIVE_ROOT}/works/${workId}/${filename}`

// ─── Aggregate sync helpers ───────────────────────────────────────────────────
// Characters, pairings, plotpoints, canon notes are stored as one aggregate JSON
// file per type per work. The file carries { entities, tombstones } so another
// device can merge per-entity — preserving its own un-uploaded additions — and
// learn which entities were deleted. (Legacy bare-array files stay readable on
// down-sync; see parseAggregate in sync.ts.)

/**
 * How long an aggregate tombstone is kept (W11 decision 7, 2026-10-07: 90 days, the longest a
 * device realistically stays offline; contently uses 60). Before W11 tombstones were never
 * collected, so every aggregate file grew forever. Past this, a tombstone is dropped from the
 * uploaded file and from the local table (sync.ts pruneTombstones). The accepted cost: a device
 * offline for longer than this can bring back an entity deleted elsewhere in the meantime.
 */
const TOMBSTONE_RETENTION_DAYS = 90
export const TOMBSTONE_RETENTION_MS = TOMBSTONE_RETENTION_DAYS * 24 * 60 * 60 * 1000

async function enqueueAggregateSync<T extends { workId: string }>(
  table: Table<T, string>,
  entityType: AggregateEntityType,
  workId: string,
  filename: string,
): Promise<void> {
  const entities = await table.where('workId').equals(workId).toArray()
  const cutoff = serverNow() - TOMBSTONE_RETENTION_MS
  const tombs = (await db.tombstones.where('[workId+entityType]').equals([workId, entityType]).toArray())
    .filter(t => t.deletedAt >= cutoff)
  await enqueueUpload({
    entityType,
    entityId: `${filename.replace('.json', '')}:${workId}`,
    workId,
    operation: 'upload',
    remotePath: aggregatePath(workId, filename),
    payload: JSON.stringify({
      entities,
      tombstones: tombs.map(t => ({ id: t.entityId, deletedAt: t.deletedAt })),
    }),
  })
}

const enqueueCharactersSync = (workId: string) => enqueueAggregateSync(db.characters, 'character',  workId, 'characters.json')
const enqueuePairingsSync   = (workId: string) => enqueueAggregateSync(db.pairings,   'pairing',    workId, 'pairings.json')
const enqueuePlotPointsSync = (workId: string) => enqueueAggregateSync(db.plotPoints, 'plotpoint',  workId, 'plotpoints.json')
const enqueueCanonNotesSync = (workId: string) => enqueueAggregateSync(db.canonNotes, 'canonnote',  workId, 'canonnotes.json')
const enqueueScenesSync     = (workId: string) => enqueueAggregateSync(db.scenes,     'scene',      workId, 'scenes.json')
// Cuts (graveyard, draft-safety layer 4) sync as a per-work aggregate exactly like
// the others. Exported so the lazy lib/cuts.ts can enqueue without pulling its
// createCut/deleteCut bodies into the eager boot bundle.
export const enqueueCutsSync = (workId: string) => enqueueAggregateSync(db.cuts, 'cut', workId, 'cuts.json')

/** Record a deletion tombstone so the next aggregate upload carries it. Exported for
 *  the lazy lib/cuts.ts (deleteCut). */
export async function tombstone(entityType: AggregateEntityType, entityId: string, workId: string): Promise<void> {
  await db.tombstones.put({ entityId, entityType, workId, deletedAt: serverNow() })
}

// ─── Work ─────────────────────────────────────────────────────────────────────

export async function createWork(
  data: Omit<Work, 'id' | 'createdAt' | 'updatedAt' | 'cTag'>
): Promise<Work> {
  const now = serverNow()
  const work: Work = { ...data, id: uuid(), createdAt: now, updatedAt: now, activeTropeIds: data.activeTropeIds ?? [] }
  await saveWork(work)
  return work
}

export async function saveWork(work: Work): Promise<void> {
  const updated: Work = { ...work, updatedAt: serverNow() }
  await db.transaction('rw', db.works, db.syncQueue, async () => {
    await db.works.put(updated)
    await enqueueUpload({
      entityType: 'work',
      entityId: work.id,
      workId: work.id,
      operation: 'upload',
      remotePath: workMetaPath(work.id),
      payload: JSON.stringify(updated),
    })
  })
}

/**
 * Re-enqueue a work's CURRENT local meta to OneDrive WITHOUT bumping updatedAt —
 * used by down-sync (D3) to heal the cloud when it discovers the cloud holds an
 * OLDER meta than local (an older edit that was uploaded later). Pushing the newer
 * local content up converges the two without the timestamp churn a full saveWork
 * would cause, and without re-triggering itself (content stamps become equal).
 */
export async function reEnqueueWorkMeta(work: Work): Promise<void> {
  await enqueueUpload({
    entityType: 'work', entityId: work.id, workId: work.id,
    operation: 'upload', remotePath: workMetaPath(work.id), payload: JSON.stringify(work),
  })
}

/** Chapter-meta sibling of reEnqueueWorkMeta (D3) — pushes the current local meta
 *  (device-local fields stripped, no updatedAt bump) to heal an older cloud copy. */
export async function reEnqueueChapterMeta(chapter: Chapter): Promise<void> {
  const meta: Partial<Chapter> = { ...chapter }
  delete meta.prose
  delete meta.proseLoaded
  delete meta.needsUpload
  delete meta.cTag
  delete meta.proseCTag
  await enqueueUpload({
    entityType: 'chapter', entityId: `${chapter.id}:meta`, workId: chapter.workId,
    operation: 'upload', remotePath: chapterMetaPath(chapter.workId, chapter.id), payload: JSON.stringify(meta),
  })
}

/**
 * Field-level patch for a Work (U2). Fresh-reads the current row INSIDE the
 * transaction and merges only the given fields — so a rename saved from one render
 * can't be reverted by a swatch/toggle click saved from an earlier, staler render
 * (each `saveWork({ ...work, oneField })` PUT the whole render-time snapshot; the
 * later blur, built from the same stale `work`, clobbered the earlier one). Mirrors
 * `saveChapterMeta`'s cure. A missing row is a no-op (deleted since the render).
 */
export async function patchWork(id: string, patch: Partial<Work>): Promise<void> {
  await db.transaction('rw', db.works, db.syncQueue, async () => {
    const current = await db.works.get(id)
    if (!current) return
    const updated: Work = { ...current, ...patch, updatedAt: serverNow() }
    await db.works.put(updated)
    await enqueueUpload({
      entityType: 'work',
      entityId: id,
      workId: id,
      operation: 'upload',
      remotePath: workMetaPath(id),
      payload: JSON.stringify(updated),
    })
  })
}

export async function deleteWork(workId: string): Promise<void> {
  const tables = [db.works, db.chapters, db.scenes, db.characters, db.pairings, db.plotPoints, db.canonNotes, db.cuts, db.tombstones, db.syncQueue, db.syncConflicts, db.trash, db.remoteTags]
  await db.transaction('rw', tables, async () => {
    // Draft-safety layer 3 (recovery shelf): capture the full entity graph into
    // `trash` BEFORE the cascade runs, so a confirmed delete is recoverable for 30
    // days. Device-local — never synced (see the TrashEntry type). If the work is
    // already gone, skip (nothing to trash).
    const work = await db.works.get(workId)
    if (work) {
      await db.trash.put({
        id: uuid(),
        deletedAt: serverNow(),
        kind: 'work',
        payload: {
          work,
          chapters:   await db.chapters.where('workId').equals(workId).toArray(),
          scenes:     await db.scenes.where('workId').equals(workId).toArray(),
          characters: await db.characters.where('workId').equals(workId).toArray(),
          pairings:   await db.pairings.where('workId').equals(workId).toArray(),
          plotPoints: await db.plotPoints.where('workId').equals(workId).toArray(),
          canonNotes: await db.canonNotes.where('workId').equals(workId).toArray(),
          cuts:       await db.cuts.where('workId').equals(workId).toArray(),
        },
      })
    }
    await db.chapters.where('workId').equals(workId).delete()
    await db.scenes.where('workId').equals(workId).delete()
    await db.characters.where('workId').equals(workId).delete()
    await db.pairings.where('workId').equals(workId).delete()
    await db.plotPoints.where('workId').equals(workId).delete()
    await db.canonNotes.where('workId').equals(workId).delete()
    await db.cuts.where('workId').equals(workId).delete()   // graveyard is per-work (layer 4)
    await db.tombstones.where('workId').equals(workId).delete()
    // Clear any open prose conflicts for this work's chapters (D4).
    await db.syncConflicts.where('workId').equals(workId).delete()
    // W11 decision 2: the meta.json delete is If-Match the version this device last saw
    // (the tag of its last meta write or merge, else the one down-sync recorded), so a
    // work edited on another device since is restored rather than deleted.
    const metaTag = (await db.remoteTags.get(workMetaPath(workId)))?.eTag ?? work?.cTag
    await db.works.delete(workId)
    // Cancel any pending uploads for this work
    await db.syncQueue.where('workId').equals(workId).delete()
    // Enqueue the cloud deletes INSIDE the transaction (after the purge above) so
    // the local delete and its propagation commit atomically — a crash in between
    // can no longer leave the OneDrive copy alive to re-download later (audit §5).
    // meta.json is deleted explicitly first so the deletion surfaces in another
    // device's delta query as a parseable deleted item; then the folder goes.
    await enqueueUpload({
      entityType: 'work',
      entityId: `delete:work:meta:${workId}`,
      workId,
      operation: 'delete',
      remotePath: workMetaPath(workId),
      ...(metaTag ? { ifMatch: metaTag } : {}),
    })
    await enqueueUpload({
      entityType: 'work',
      entityId: `delete:work:${workId}`,
      workId,
      operation: 'delete',
      remotePath: `${QUIL_ONEDRIVE_ROOT}/works/${workId}`,
    })
  })
}

// ─── Chapter ──────────────────────────────────────────────────────────────────

export async function createChapter(
  data: Omit<Chapter, 'id' | 'createdAt' | 'updatedAt' | 'cTag'>
): Promise<Chapter> {
  const now = serverNow()
  const chapter: Chapter = {
    ...data,
    id: uuid(),
    createdAt: now,
    updatedAt: now,
    tropeBeatRefs: data.tropeBeatRefs ?? [],
  }
  await saveChapter(chapter)
  return chapter
}

export async function saveChapter(chapter: Chapter): Promise<void> {
  const updated: Chapter = {
    ...chapter,
    updatedAt: serverNow(),
    wordCount: countProseWords(chapter.prose),
    proseLoaded: true,   // a local save means this device holds the prose
    needsUpload: false,  // this save enqueues the upload, so the D2 dirty marker is cleared
    lastEditedDevice: deviceNameRef.current || undefined,   // rides the meta (device-names)
  }
  // `proseLoaded`/`needsUpload` are device-local cache flags and `cTag` is this
  // device's If-Match token for the prose file — all per-device state, never synced.
  // A cTag that rode along in meta.json would overwrite the receiving device's
  // token with a stale value and false-trigger 412 conflicts (audit 3.1).
  const { prose, ...meta } = updated
  delete (meta as Partial<Chapter>).proseLoaded
  delete (meta as Partial<Chapter>).needsUpload
  delete (meta as Partial<Chapter>).cTag
  delete (meta as Partial<Chapter>).proseCTag

  await db.transaction('rw', [db.chapters, db.works, db.syncQueue], async () => {
    // The If-Match tokens are this device's, and a sync may have advanced them since the
    // caller read its copy: take them from the row as it is NOW, never the caller's
    // snapshot, or a stale tag goes back in and the next upload 412s into a false conflict.
    const current = await db.chapters.get(chapter.id)
    if (current) {
      if (current.cTag !== undefined) updated.cTag = current.cTag; else delete updated.cTag
      if (current.proseCTag !== undefined) updated.proseCTag = current.proseCTag; else delete updated.proseCTag
    }
    await db.chapters.put(updated)
    // Touch work.updatedAt so Dashboard "recent" sort reflects latest writing activity
    await db.works.update(chapter.workId, { updatedAt: updated.updatedAt })
    // Prose (.prose.json) — checked for conflicts on upload
    await enqueueUpload({
      entityType: 'chapter',
      entityId: chapter.id,
      workId: chapter.workId,
      operation: 'upload',
      remotePath: chapterProsePath(chapter.workId, chapter.id),
      payload: prose,
    })
    // Metadata (.json) — LWW, no conflict check
    await enqueueUpload({
      entityType: 'chapter',
      entityId: `${chapter.id}:meta`,
      workId: chapter.workId,
      operation: 'upload',
      remotePath: chapterMetaPath(chapter.workId, chapter.id),
      payload: JSON.stringify(meta),
    })
  })
}

/**
 * Crash-insurance fast path for the hide/unload flush (audit 2.5). Uses the RAW
 * IndexedDB API so the readwrite transaction opens SYNCHRONOUSLY inside the
 * pagehide/visibilitychange handler — that is the one pattern browsers commit
 * even while the page is being torn down. Dexie's own update() schedules its
 * transaction through promise machinery a task later, which loses the race on
 * an immediate reload/close (caught by the e2e reload-persistence journeys).
 * The get→put stays inside the one transaction; mutating only the prose fields
 * cannot clobber concurrent metadata edits. The caller follows up with a normal
 * saveChapter for the work-touch + upload enqueue; if the page dies first, the
 * prose is already safe locally and the next save re-enqueues the upload.
 */
export function flushChapterProse(chapterId: string, prose: string): Promise<void> {
  const finishStamp = serverNow()
  const wordCount = countProseWords(prose)
  const raw: IDBDatabase | undefined = (db as unknown as { backendDB?: () => IDBDatabase }).backendDB?.()
  if (raw) {
    return new Promise<void>((resolve) => {
      try {
        const tx = raw.transaction('chapters', 'readwrite')
        const store = tx.objectStore('chapters')
        const req = store.get(chapterId)
        req.onsuccess = () => {
          const ch = req.result as Chapter | undefined
          // needsUpload:true is the durable dirty marker (D2) — written in the SAME
          // synchronous transaction as the prose, so a crash before the follow-up
          // saveChapter still leaves proof the body needs uploading.
          if (ch) store.put({ ...ch, prose, wordCount, proseLoaded: true, needsUpload: true, updatedAt: finishStamp })
        }
        tx.oncomplete = () => resolve()
        // A genuine transaction failure — quota exceeded, disk/IO error — must not be
        // swallowed (D10): the writer's last words just failed to persist locally.
        // Surface it (console + the sync monitor's write-error state drives the pill)
        // so a full disk is visible somewhere instead of silently losing prose.
        tx.onerror = () => {
          console.warn('[Quil] flushChapterProse transaction failed', tx.error)
          logSync('write', false, `flush failed: ${tx.error?.name ?? 'unknown'}`)
          resolve()
        }
        // onabort is usually benign page-teardown (the follow-up saveChapter re-drives
        // the upload), so it stays quiet — only a true error is worth surfacing.
        tx.onabort = () => resolve()
      } catch (err) {
        console.warn('[Quil] flushChapterProse could not open a transaction', err)
        resolve() // e.g. db closed mid-teardown — the follow-up saveChapter is the fallback
      }
    })
  }
  // Dexie fallback (db not open yet — shouldn't happen with a mounted editor).
  return db.chapters
    .update(chapterId, { prose, wordCount, proseLoaded: true, needsUpload: true, updatedAt: finishStamp })
    .then(() => undefined)
}

/**
 * Convert any surviving crash-flush markers into real upload queue entries (D2).
 * `flushChapterProse` persists prose + `needsUpload:true` synchronously during page
 * teardown but does NOT enqueue the upload (that is the follow-up saveChapter's job).
 * If the page dies in between, this sweep — run at launch and safe to call again on
 * any save — re-drives each marked chapter through saveChapter, which enqueues the
 * prose+meta upload and clears the marker. Returns how many chapters were swept.
 */
export async function sweepCrashFlushedProse(): Promise<number> {
  const marked = await db.chapters.filter(c => c.needsUpload === true).toArray()
  for (const ch of marked) await saveChapter(ch)
  return marked.length
}

/**
 * Persist a metadata-only chapter change (title, status, order, planning refs,
 * author's notes) WITHOUT re-uploading the prose body. A full saveChapter would
 * PUT the unchanged prose file too, advancing its OneDrive etag and invalidating every
 * other device's cached body for a chapter whose text never changed (audit 3.2)
 * — and it keeps 412s meaning "prose actually diverged".
 *
 * The body + device-local fields (prose, wordCount, proseLoaded, cTag) are taken
 * from the CURRENT Dexie row, not the caller's snapshot — an autosave may have
 * landed since the snapshot was taken, and a meta write must never regress prose.
 */
export async function saveChapterMeta(chapter: Chapter): Promise<void> {
  await db.transaction('rw', [db.chapters, db.works, db.syncQueue], async () => {
    const current = await db.chapters.get(chapter.id)
    const updated: Chapter = {
      ...chapter,
      prose: current?.prose ?? chapter.prose,
      wordCount: current?.wordCount ?? chapter.wordCount,
      proseLoaded: current?.proseLoaded ?? chapter.proseLoaded,
      // A metadata write must never clear a pending prose dirty marker (D2): keep
      // whatever the current row holds so a crash-flushed body still gets swept.
      needsUpload: current?.needsUpload ?? chapter.needsUpload,
      cTag: current?.cTag ?? chapter.cTag,
      // Keep the last PROSE author's device label; a meta-only tweak (status, hook,
      // posted) shouldn't reattribute the content to whoever tweaked it.
      lastEditedDevice: current?.lastEditedDevice ?? chapter.lastEditedDevice,
      updatedAt: serverNow(),
    }
    // Same synced-meta shape as saveChapter: no prose, no device-local fields.
    const meta: Partial<Chapter> = { ...updated }
    delete meta.prose
    delete meta.proseLoaded
    delete meta.needsUpload
    delete meta.cTag
    delete meta.proseCTag

    await db.chapters.put(updated)
    await db.works.update(chapter.workId, { updatedAt: updated.updatedAt })
    await enqueueUpload({
      entityType: 'chapter',
      entityId: `${chapter.id}:meta`,
      workId: chapter.workId,
      operation: 'upload',
      remotePath: chapterMetaPath(chapter.workId, chapter.id),
      payload: JSON.stringify(meta),
    })
  })
}

/**
 * Field-level patch for a chapter's METADATA (U2 / publishing-workflow prereq).
 * The chapter sibling of `patchWork`: fresh-reads the current row inside the
 * transaction and merges only the given fields, so a write built from a stale
 * render can't clobber a field another render just changed. Never touches prose
 * or the device-local flags (that's the whole point of the meta path). A missing
 * row is a no-op. Delegates to `saveChapterMeta` so the serialized-shape +
 * work.updatedAt bump + enqueue logic lives in exactly one place.
 */
export async function patchChapterMeta(id: string, patch: Partial<Chapter>): Promise<void> {
  const current = await db.chapters.get(id)
  if (!current) return
  await saveChapterMeta({ ...current, ...patch })
}

// ─── Scene ────────────────────────────────────────────────────────────────────
// Sub-unit of a Chapter (§7, S4). Synced as an aggregate file (scenes.json).

export async function createScene(
  data: Omit<Scene, 'id' | 'createdAt' | 'updatedAt' | 'cTag'>
): Promise<Scene> {
  const now = serverNow()
  const scene: Scene = {
    ...data,
    id: uuid(),
    createdAt: now,
    updatedAt: now,
    characterIds: data.characterIds ?? [],
  }
  await saveScene(scene)
  return scene
}

async function saveScene(scene: Scene): Promise<void> {
  const updated: Scene = { ...scene, updatedAt: serverNow() }
  await db.transaction('rw', db.scenes, db.tombstones, db.syncQueue, async () => {
    await db.scenes.put(updated)
    await enqueueScenesSync(scene.workId)
  })
}

/** Rename a scene (PG1 — the navigator chips are no longer append-only). Empty
 *  clears the custom title so the chip falls back to "Scene N". */
export async function renameScene(sceneId: string, title: string): Promise<void> {
  await db.transaction('rw', db.scenes, db.tombstones, db.syncQueue, async () => {
    const s = await db.scenes.get(sceneId)
    if (!s) return
    await db.scenes.put({ ...s, title: title.trim() || undefined, updatedAt: serverNow() })
    await enqueueScenesSync(s.workId)
  })
}

export async function deleteScene(sceneId: string, workId: string): Promise<void> {
  await db.transaction('rw', [db.scenes, db.plotPoints, db.tombstones, db.syncQueue], async () => {
    await db.scenes.delete(sceneId)
    await tombstone('scene', sceneId, workId)
    // Detach any plot points that lived in this scene (they return to unassigned;
    // chapterId is left as-is so they stay visible on the chapter).
    const orphaned = await db.plotPoints.where('workId').equals(workId).toArray()
    for (const pp of orphaned) {
      if (pp.sceneId === sceneId) {
        await db.plotPoints.put({ ...pp, sceneId: undefined, updatedAt: serverNow() })
      }
    }
    await enqueueScenesSync(workId)
    await enqueuePlotPointsSync(workId)
  })
}

// ─── Character ────────────────────────────────────────────────────────────────

export async function createCharacter(
  data: Omit<Character, 'id' | 'createdAt' | 'updatedAt' | 'cTag'>
): Promise<Character> {
  const now = serverNow()
  const character: Character = {
    ...data,
    id: uuid(),
    createdAt: now,
    updatedAt: now,
    aliases: data.aliases ?? [],
    isMajor: data.isMajor ?? true,
  }
  await saveCharacter(character)
  return character
}

// Internal since U2 — createCharacter uses it; all detail-sheet callers moved to
// patchCharacter (field-level merge). Kept as the full-object create path.
async function saveCharacter(character: Character): Promise<void> {
  const updated: Character = { ...character, updatedAt: serverNow() }
  await db.transaction('rw', db.characters, db.tombstones, db.syncQueue, async () => {
    await db.characters.put(updated)
    await enqueueCharactersSync(character.workId)
  })
}

/** Field-level patch for a Character (U2 — see patchWork). Fresh-reads inside the
 *  transaction so a detail-sheet field save can't revert a sibling field's earlier save. */
export async function patchCharacter(id: string, patch: Partial<Character>): Promise<void> {
  await db.transaction('rw', db.characters, db.tombstones, db.syncQueue, async () => {
    const current = await db.characters.get(id)
    if (!current) return
    await db.characters.put({ ...current, ...patch, updatedAt: serverNow() })
    await enqueueCharactersSync(current.workId)
  })
}

export async function deleteCharacter(characterId: string, workId: string): Promise<void> {
  await db.transaction('rw', [db.characters, db.pairings, db.canonNotes, db.plotPoints, db.scenes, db.tombstones, db.syncQueue], async () => {
    await db.characters.delete(characterId)
    await tombstone('character', characterId, workId)

    // Cascade (boards audit Q4c): a deleted character must not leave ghosts.
    // Pairings naming it would otherwise render as raw ids on the export page's
    // Relationships list; its canon notes would sit invisible in the aggregate
    // forever; and its id would linger inside plot points / scenes.
    const pairings = await db.pairings.where('workId').equals(workId).toArray()
    let touchedPairings = false
    for (const p of pairings) {
      if (p.characterIds.includes(characterId)) {
        await db.pairings.delete(p.id)
        await tombstone('pairing', p.id, workId)
        touchedPairings = true
      }
    }
    const notes = await db.canonNotes.where('characterId').equals(characterId).toArray()
    for (const n of notes) {
      await db.canonNotes.delete(n.id)
      await tombstone('canonnote', n.id, workId)
    }
    const pps = await db.plotPoints.where('workId').equals(workId).toArray()
    let touchedPlotPoints = false
    for (const pp of pps) {
      if (pp.aboutCharacterIds.includes(characterId)) {
        await db.plotPoints.put({ ...pp, aboutCharacterIds: pp.aboutCharacterIds.filter(id => id !== characterId), updatedAt: serverNow() })
        touchedPlotPoints = true
      }
    }
    const scenes = await db.scenes.where('workId').equals(workId).toArray()
    let touchedScenes = false
    for (const s of scenes) {
      if (s.characterIds.includes(characterId)) {
        await db.scenes.put({ ...s, characterIds: s.characterIds.filter(id => id !== characterId), updatedAt: serverNow() })
        touchedScenes = true
      }
    }

    await enqueueCharactersSync(workId)
    if (touchedPairings) await enqueuePairingsSync(workId)
    if (notes.length > 0) await enqueueCanonNotesSync(workId)
    if (touchedPlotPoints) await enqueuePlotPointsSync(workId)
    if (touchedScenes) await enqueueScenesSync(workId)
  })
}

// ─── Pairing ──────────────────────────────────────────────────────────────────

export async function createPairing(
  data: Omit<Pairing, 'id' | 'createdAt' | 'updatedAt' | 'cTag'>
): Promise<Pairing> {
  const now = serverNow()
  const pairing: Pairing = {
    ...data,
    id: uuid(),
    createdAt: now,
    updatedAt: now,
    characterIds: data.characterIds ?? [],
  }
  await savePairing(pairing)
  return pairing
}

// Internal since U2 — createPairing uses it; edge-menu callers moved to patchPairing.
async function savePairing(pairing: Pairing): Promise<void> {
  const updated: Pairing = { ...pairing, updatedAt: serverNow() }
  await db.transaction('rw', db.pairings, db.tombstones, db.syncQueue, async () => {
    await db.pairings.put(updated)
    await enqueuePairingsSync(pairing.workId)
  })
}

/** Field-level patch for a Pairing (U2 — see patchWork). */
export async function patchPairing(id: string, patch: Partial<Pairing>): Promise<void> {
  await db.transaction('rw', db.pairings, db.tombstones, db.syncQueue, async () => {
    const current = await db.pairings.get(id)
    if (!current) return
    await db.pairings.put({ ...current, ...patch, updatedAt: serverNow() })
    await enqueuePairingsSync(current.workId)
  })
}

export async function deletePairing(pairingId: string, workId: string): Promise<void> {
  await db.transaction('rw', [db.pairings, db.tombstones, db.syncQueue], async () => {
    await db.pairings.delete(pairingId)
    await tombstone('pairing', pairingId, workId)
    await enqueuePairingsSync(workId)
  })
}

// ─── PlotPoint ────────────────────────────────────────────────────────────────

export async function createPlotPoint(
  data: Omit<PlotPoint, 'id' | 'createdAt' | 'updatedAt' | 'cTag'>
): Promise<PlotPoint> {
  const now = serverNow()
  const plotPoint: PlotPoint = {
    ...data,
    id: uuid(),
    createdAt: now,
    updatedAt: now,
    aboutCharacterIds: data.aboutCharacterIds ?? [],
  }
  await savePlotPoint(plotPoint)
  return plotPoint
}

export async function savePlotPoint(plotPoint: PlotPoint): Promise<void> {
  const updated: PlotPoint = { ...plotPoint, updatedAt: serverNow() }
  await db.transaction('rw', db.plotPoints, db.tombstones, db.syncQueue, async () => {
    await db.plotPoints.put(updated)
    await enqueuePlotPointsSync(plotPoint.workId)
  })
}

/** Field-level patch for a PlotPoint (U2 — see patchWork). */
export async function patchPlotPoint(id: string, patch: Partial<PlotPoint>): Promise<void> {
  await db.transaction('rw', db.plotPoints, db.tombstones, db.syncQueue, async () => {
    const current = await db.plotPoints.get(id)
    if (!current) return
    await db.plotPoints.put({ ...current, ...patch, updatedAt: serverNow() })
    await enqueuePlotPointsSync(current.workId)
  })
}

export async function deletePlotPoint(plotPointId: string, workId: string): Promise<void> {
  await db.transaction('rw', [db.plotPoints, db.tombstones, db.syncQueue], async () => {
    await db.plotPoints.delete(plotPointId)
    await tombstone('plotpoint', plotPointId, workId)
    await enqueuePlotPointsSync(workId)
  })
}

export async function deleteChapter(chapterId: string, workId: string): Promise<void> {
  let hadScenes = false
  let touchedPlotPoints = false
  await db.transaction('rw', [db.chapters, db.scenes, db.plotPoints, db.tombstones, db.syncQueue, db.syncConflicts, db.trash], async () => {
    // Draft-safety layer 3: capture the chapter (incl. prose) + its scenes into
    // `trash` before the cascade, so a confirmed chapter delete is recoverable.
    const chapter = await db.chapters.get(chapterId)
    // Cascade the chapter's scenes (aggregate-synced → tombstone each) and clear
    // the sceneId on any plot points that lived in them.
    const scenes = await db.scenes.where('chapterId').equals(chapterId).toArray()
    if (chapter) {
      await db.trash.put({
        id: uuid(), deletedAt: serverNow(), kind: 'chapter',
        payload: { chapters: [chapter], scenes },
      })
    }
    for (const s of scenes) {
      await db.scenes.delete(s.id)
      await tombstone('scene', s.id, workId)
      hadScenes = true
    }
    const sceneIds = new Set(scenes.map(s => s.id))
    if (sceneIds.size > 0) {
      const pps = await db.plotPoints.where('workId').equals(workId).toArray()
      for (const pp of pps) {
        if (pp.sceneId && sceneIds.has(pp.sceneId)) {
          await db.plotPoints.put({ ...pp, sceneId: undefined, updatedAt: serverNow() })
          touchedPlotPoints = true
        }
      }
    }
    await db.chapters.delete(chapterId)
    // Clear any open prose conflict for this chapter, or getWorkSyncState would keep
    // reporting 'conflict' for a chapter that no longer exists (D4).
    await db.syncConflicts.where('id').equals(chapterId).delete()
    await db.syncQueue.where('entityId').equals(chapterId).delete()
    await db.syncQueue.where('entityId').equals(`${chapterId}:meta`).delete()
    // Enqueue the aggregate re-syncs INSIDE the transaction so the tombstone and
    // its upload are atomic — a crash can't leave the deletion un-propagated (L1).
    if (hadScenes) await enqueueScenesSync(workId)
    if (touchedPlotPoints) await enqueuePlotPointsSync(workId)
    // Cloud deletes too, for the same atomicity (audit §5, same as deleteWork).
    await enqueueUpload({
      entityType: 'chapter',
      entityId: `delete:chapter:prose:${chapterId}`,
      workId,
      operation: 'delete',
      remotePath: chapterProsePath(workId, chapterId),
      // W11 decision 2: If-Match the body this device last saw, so a chapter edited on
      // another device since is restored rather than deleted. A body never downloaded
      // here has no such version (`unseen`, see SyncQueueEntry).
      ...(chapter?.cTag ? { ifMatch: chapter.cTag } : chapter?.proseLoaded === false ? { unseen: true as const } : {}),
    })
    await enqueueUpload({
      entityType: 'chapter',
      entityId: `delete:chapter:meta:${chapterId}`,
      workId,
      operation: 'delete',
      remotePath: chapterMetaPath(workId, chapterId),
    })
  })
}

// ─── Prose conflicts ────────────────────────────────────────────────────────────
// A refused prose upload is a shared ConflictRecord in db.syncConflicts (W11). Its
// resolution moved to lib/syncConflicts.ts (resolveProse, over the shared
// applyResolution contract), which loads only when a conflict exists.

// ─── Snapshots (draft-safety layer 1) ────────────────────────────────────────────
// Device-local point-in-time copies of a chapter's prose. NEVER synced — see the
// Snapshot type doc. Automatic snapshots are pruned to the newest N per chapter;
// manual ones are kept forever.

const MAX_AUTO_SNAPSHOTS_PER_CHAPTER = 10

/**
 * Capture the current prose of a chapter as a Snapshot. Reads the chapter row and
 * copies its prose + wordCount. Prunes automatic snapshots (all reasons except
 * 'manual') to the newest N per chapter inside the same transaction. Returns the
 * snapshot, or null if the chapter no longer exists.
 */
export async function takeSnapshot(
  chapterId: string,
  reason: SnapshotReason,
  label?: string,
  override?: { prose: string },   // snapshot arbitrary prose (e.g. a conflict's remote version)
): Promise<Snapshot | null> {
  return db.transaction('rw', db.chapters, db.snapshots, async () => {
    const ch = await db.chapters.get(chapterId)
    if (!ch) return null
    const prose = override?.prose ?? ch.prose ?? ''
    const snap: Snapshot = {
      id: uuid(),
      workId: ch.workId,
      chapterId,
      prose,
      wordCount: override ? countProseWords(prose) : (ch.wordCount ?? countProseWords(prose)),
      takenAt: Date.now(),   // device-local, never synced — wall-clock is correct here
      reason,
      ...(label ? { label } : {}),
    }
    await db.snapshots.put(snap)
    if (reason !== 'manual') {
      // Retention: keep only the newest N automatic snapshots per chapter. Manual
      // snapshots are never counted or pruned (the brief: "never auto-pruned").
      const autos = (await db.snapshots.where('chapterId').equals(chapterId).toArray())
        .filter(s => s.reason !== 'manual')
        .sort((a, b) => b.takenAt - a.takenAt)
      for (const stale of autos.slice(MAX_AUTO_SNAPSHOTS_PER_CHAPTER)) {
        await db.snapshots.delete(stale.id)
      }
    }
    return snap
  })
}

// (restoreSnapshot + deleteSnapshot live in the lazy lib/proseVersions.ts — they're
//  reachable only from the lazy VersionsPanel, so keeping them out of storage.ts
//  keeps the eager boot bundle under the hard 600 KiB budget.)

// ─── Trash / recovery shelf (draft-safety layer 3) ───────────────────────────────
// Capture happens in deleteWork/deleteChapter (before the cascade, above). Restore,
// hard-delete, AND the boot purge all live in the lazy lib/trash.ts — reachable only
// from Settings / a dynamic boot import — so they stay out of the eager boot bundle.

// ─── CanonNote ────────────────────────────────────────────────────────────────

export async function createCanonNote(
  data: Omit<CanonNote, 'id' | 'createdAt' | 'updatedAt'>
): Promise<CanonNote> {
  const now = serverNow()
  const note: CanonNote = { ...data, id: uuid(), createdAt: now, updatedAt: now }
  await saveCanonNote(note)
  return note
}

async function saveCanonNote(note: CanonNote): Promise<void> {
  const updated: CanonNote = { ...note, updatedAt: serverNow() }
  await db.transaction('rw', db.canonNotes, db.tombstones, db.syncQueue, async () => {
    await db.canonNotes.put(updated)
    await enqueueCanonNotesSync(note.workId)
  })
}

export async function deleteCanonNote(noteId: string, workId: string): Promise<void> {
  await db.transaction('rw', [db.canonNotes, db.tombstones, db.syncQueue], async () => {
    await db.canonNotes.delete(noteId)
    await tombstone('canonnote', noteId, workId)
    await enqueueCanonNotesSync(workId)
  })
}

// ─── OneDrive-first migration reconcile (Phase 2) ───────────────────────────────

/**
 * One-time cutover reconcile: enqueue an upload for **every** local entity so
 * pre-existing local-only data (created before the sign-in gate) reaches OneDrive.
 *
 * MUST run **after** an initial down-sync — by then local holds the cloud union,
 * so pushing the (whole-file) aggregates up can't drop cloud-only entities. On a
 * genuinely fresh device (no local works) it's a no-op. Idempotent: the queue
 * de-dupes by (entityId, remotePath). Returns the number of files queued.
 */
export async function reconcileLocalToCloud(): Promise<number> {
  const works = await db.works.toArray()
  let queued = 0
  for (const w of works) queued += await reconcileWorkRow(w)
  return queued
}

/** One work's half of reconcileLocalToCloud: everything of this work with no cTag goes up, and
 *  every aggregate. Used by the W11 recreate path when a whole work was deleted on another device
 *  and an edit here brought it back (its tags were cleared first, so it all goes up as creates). */
export async function reconcileWork(workId: string): Promise<number> {
  const w = await db.works.get(workId)
  return w ? reconcileWorkRow(w) : 0
}

async function reconcileWorkRow(w: Work): Promise<number> {
  let queued = 0
  {
    // A cTag means the entity already synced to OneDrive; only push LOCAL-ONLY
    // works/chapters (no cTag). Re-pushing synced files is pointless and, for
    // prose (If-Match'd), false-triggers 412 conflicts against a stale cTag. Genuine
    // local edits to synced entities already sit in the queue; the caller has also
    // just merged the cloud in (down-sync + mergeLocalWorkAggregates), so this can't
    // orphan cloud-only data. Aggregates are whole-file LWW and carry any local-only
    // entities of an otherwise-synced work up, so they're always refreshed.
    if (!w.cTag) {
      await enqueueUpload({
        entityType: 'work', entityId: w.id, workId: w.id, operation: 'upload',
        remotePath: workMetaPath(w.id), payload: JSON.stringify(w),
      })
      queued += 1
    }

    const chapters = await db.chapters.where('workId').equals(w.id).toArray()
    for (const ch of chapters) {
      if (ch.cTag) continue // already synced — skip prose + meta
      const { prose, ...meta } = ch // proseLoaded + cTag are device-local, never synced
      delete (meta as Partial<Chapter>).proseLoaded
      delete (meta as Partial<Chapter>).cTag
      delete (meta as Partial<Chapter>).proseCTag
      if (prose) { // skip an empty local-only chapter's prose (nothing to migrate; B10)
        await enqueueUpload({
          entityType: 'chapter', entityId: ch.id, workId: w.id, operation: 'upload',
          remotePath: chapterProsePath(w.id, ch.id), payload: prose,
        })
        queued += 1
      }
      await enqueueUpload({
        entityType: 'chapter', entityId: `${ch.id}:meta`, workId: w.id, operation: 'upload',
        remotePath: chapterMetaPath(w.id, ch.id), payload: JSON.stringify(meta),
      })
      queued += 1
    }

    await enqueueCharactersSync(w.id)
    await enqueuePairingsSync(w.id)
    await enqueuePlotPointsSync(w.id)
    await enqueueCanonNotesSync(w.id)
    await enqueueScenesSync(w.id)
    await enqueueCutsSync(w.id)
    queued += 6
  }
  return queued
}
